/**
 * Collection page — client-side "Style" filter.
 *
 * Source data: the same `bundle-design-options` response
 * team-design.swatches.js already fetches/caches per card (keyed by the
 * bundle code embedded in each card's SKU). Some `bundlePatternSets[]`
 * entries carry a `categories` array (e.g. { value: "CLASSIC", label:
 * "Classic" }) tagging that specific pattern set's style. A card's style is
 * whatever its *matched/shown* pattern set carries — same resolution rule
 * swatches.js uses (PS-pinned code, else the bundle's first pattern set),
 * shared via `window.CollectionDesignOptions.resolveMatchedPattern`.
 *
 * This is unrelated to Shopify's native faceted search (snippets/facets.liquid
 * + assets/facets.js) — that system filters on product tags/metafields
 * configured in Admin and has no knowledge of this externally-sourced
 * pattern-set category data, so filtering here is done entirely client-side
 * against the cards already rendered on the page (no server round trip).
 *
 * Desktop/tablet only (>=880px, matching the Quick Customization desktop
 * breakpoint) — not built for mobile in this pass.
 */
(function () {
  'use strict';

  var DESKTOP_QUERY = '(min-width: 880px)';
  // URL param that preselects style categories on load. Value is a
  // comma-separated list of category KEYS (uppercased), e.g.
  //   ?styles=CLASSIC,MODERN
  // Read on init + popstate (stylesFromLocation) and written back on every
  // selection change (pushStylesToUrl), so this single constant is the
  // sole source of truth for the param name.
  var STYLE_PARAM   = 'styles';

  var selected = new Set(); // uppercase category values currently selected
  var entries  = [];        // [{ card, gridItem, categories: [{value,label}] }]

  function isDesktop() {
    return window.matchMedia(DESKTOP_QUERY).matches;
  }

  // ─── URL helpers ────────────────────────────────────────────────────────────
  function stylesFromLocation() {
    var params = new URLSearchParams(window.location.search);
    var raw = params.get(STYLE_PARAM);
    if (!raw) return [];
    return raw
      .split(',')
      .map(function (v) { return v.trim().toUpperCase(); })
      .filter(Boolean);
  }

  function pushStylesToUrl() {
    var url = new URL(window.location.href);
    if (selected.size) {
      url.searchParams.set(STYLE_PARAM, Array.from(selected).join(','));
    } else {
      url.searchParams.delete(STYLE_PARAM);
    }
    history.pushState({}, '', url.toString());
  }

  // Drop any selected tokens that don't correspond to a real category on
  // this page — e.g. a typo'd ?styles= value ("PL" when only "PLO" exists).
  // Without this, renderPills/applyVisibility act on the phantom token and a
  // dead pill shows above the grid. Returns true if anything was removed.
  function pruneSelectedToIndex(index) {
    var changed = false;
    selected.forEach(function (v) {
      if (!index.has(v)) { selected.delete(v); changed = true; }
    });
    return changed;
  }

  // ─── Data resolution ────────────────────────────────────────────────────────
  function collectEntries() {
    var grid = document.getElementById('product-grid');
    if (!grid) return [];
    return Array.from(grid.querySelectorAll('.card-wrapper[data-sku]')).map(function (card) {
      return { card: card, gridItem: card.closest('.grid__item') || card, categories: [] };
    });
  }

  function resolveCategories(list) {
    var cdo = window.CollectionDesignOptions;
    if (!cdo) return Promise.resolve(list);

    var parsedList = list.map(function (entry) { return cdo.parseSku(entry.card.dataset.sku || ''); });
    var uniqueCodes = [];
    var seen = Object.create(null);
    parsedList.forEach(function (p) {
      if (p.code && !seen[p.code]) { seen[p.code] = true; uniqueCodes.push(p.code); }
    });

    return Promise.all(uniqueCodes.map(cdo.fetch)).then(function () {
      return Promise.all(
        list.map(function (entry, i) {
          var parsed = parsedList[i];
          if (!parsed.code) return null;
          return cdo.fetch(parsed.code).then(function (data) {
            if (!data) return;
            var matched = cdo.resolveMatchedPattern(data.bundlePatternSets, parsed.ps);
            var categories = (matched && matched.categories) || [];
            // Match/URL token is the human-readable label (the category
            // "Value", e.g. "Classic"), NOT the internal code/key (c.value,
            // e.g. "CLASSIC"). Uppercased so ?styles= matching is
            // case-insensitive; the original label is kept for display.
            entry.categories = categories
              .filter(function (c) { return c && (c.label || c.value); })
              .map(function (c) {
                var label = c.label || c.value;
                return { value: String(label).toUpperCase(), label: label };
              });
          });
        })
      ).then(function () { return list; });
    });
  }

  function buildCategoryIndex(list) {
    var index = new Map(); // value -> { label, count }
    list.forEach(function (entry) {
      entry.categories.forEach(function (cat) {
        if (!index.has(cat.value)) index.set(cat.value, { label: cat.label, count: 0 });
        index.get(cat.value).count += 1;
      });
    });
    return index;
  }

  // ─── Rendering ──────────────────────────────────────────────────────────────
  function bindToggle() {
    var toggle = document.getElementById('collection-style-filters__toggle');
    if (!toggle || toggle.dataset.bound) return;
    toggle.dataset.bound = '1';
    toggle.addEventListener('click', function () {
      var expanded = toggle.getAttribute('aria-expanded') !== 'false';
      toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    });
  }

  function renderPicker(index) {
    var asideEl = document.getElementById('collection-style-filters');
    var listEl  = document.getElementById('collection-style-filters__list');
    if (!asideEl || !listEl) return;

    if (index.size < 1) {
      asideEl.hidden = true;
      listEl.innerHTML = '';
      return;
    }

    bindToggle();

    var sortedValues = Array.from(index.keys()).sort(function (a, b) {
      return index.get(a).label.localeCompare(index.get(b).label);
    });

    // "All Styles (n)" clear-all row — only for the simple Quick
    // Customization version (the aside carries data-all-styles, set by
    // snippets/collection-style-filters.liquid). Checked while no style
    // is selected; checking it clears the current selection.
    var allRowHtml = '';
    if (asideEl.dataset.allStyles) {
      allRowHtml =
        '<li class="style-filter__row">' +
        '<label class="style-filter__option" for="CollectionStyleFilter-all">' +
        '<input type="checkbox" class="style-filter__checkbox" data-style-all="1" id="CollectionStyleFilter-all"' + (selected.size ? '' : ' checked') + '>' +
        '<span class="style-filter__box" aria-hidden="true"></span>' +
        '<span class="style-filter__label-group">' +
        '<span class="style-filter__text">All Styles</span>' +
        '<span class="style-filter__count">(' + entries.length + ')</span>' +
        '</span>' +
        '</label>' +
        '</li>';
    }

    listEl.innerHTML = allRowHtml + sortedValues.map(function (value, i) {
      var entry = index.get(value);
      var id = 'CollectionStyleFilter-' + i;
      var checked = selected.has(value) ? ' checked' : '';
      return (
        '<li class="style-filter__row">' +
        '<label class="style-filter__option" for="' + id + '">' +
        '<input type="checkbox" class="style-filter__checkbox" data-style-value="' + value + '" id="' + id + '"' + checked + '>' +
        '<span class="style-filter__box" aria-hidden="true"></span>' +
        '<span class="style-filter__label-group">' +
        '<span class="style-filter__text">' + escapeHtml(entry.label) + '</span>' +
        '<span class="style-filter__count">(' + entry.count + ')</span>' +
        '</span>' +
        '</label>' +
        '</li>'
      );
    }).join('');

    listEl.querySelectorAll('input[type="checkbox"]').forEach(function (input) {
      input.addEventListener('change', function () {
        if (input.dataset.styleAll) {
          // Clear-all row: any interaction resets to "no filter";
          // syncPickerState re-checks it when the selection is empty.
          selected.clear();
        } else {
          var value = input.dataset.styleValue;
          if (input.checked) selected.add(value); else selected.delete(value);
        }
        onSelectionChange(index);
      });
    });

    asideEl.hidden = false;
  }

  // Counts never change with selection (page-scoped, computed once), so a
  // selection change only needs to sync checked state — not rebuild the
  // list — to avoid stealing focus from the checkbox the user just used.
  function syncPickerState() {
    var listEl = document.getElementById('collection-style-filters__list');
    if (!listEl) return;
    listEl.querySelectorAll('input[type="checkbox"]').forEach(function (input) {
      if (input.dataset.styleAll) {
        input.checked = !selected.size;
        return;
      }
      input.checked = selected.has(input.dataset.styleValue);
    });
  }

  function renderPills(index) {
    var pillsEl = document.getElementById('collection-style-filters__pills');
    if (!pillsEl) return;

    if (!selected.size) {
      pillsEl.hidden = true;
      pillsEl.innerHTML = '';
      return;
    }

    pillsEl.innerHTML = Array.from(selected).map(function (value) {
      var label = index.has(value) ? index.get(value).label : value;
      return (
        '<button type="button" class="style-filter-pill" data-remove-style="' + value + '">' +
        '<span class="style-filter-pill__text">' + escapeHtml(label) + ' Style</span>' +
        '<svg class="style-filter-pill__remove" aria-hidden="true" focusable="false" viewBox="0 0 10 10" width="10" height="10"><path fill="currentColor" d="M1 1l8 8M9 1l-8 8" stroke="currentColor" stroke-width="1.2"/></svg>' +
        '</button>'
      );
    }).join('');

    pillsEl.querySelectorAll('[data-remove-style]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        selected.delete(btn.dataset.removeStyle);
        onSelectionChange(buildCategoryIndex(entries));
      });
    });

    pillsEl.hidden = false;
  }

  function applyVisibility() {
    entries.forEach(function (entry) {
      var matches = !selected.size || entry.categories.some(function (c) { return selected.has(c.value); });
      entry.gridItem.classList.toggle('grid__item--style-hidden', !matches);
    });
  }

  // Only scroll if the grid's top has actually scrolled past the viewport
  // (i.e. the shopper is looking at the sidebar further down the page) —
  // avoids a jarring re-scroll when the grid is already in view. Prefers
  // the applied-filter pills row (sits just above the grid) as the scroll
  // anchor when visible, so the pills stay in view too instead of landing
  // right at the top edge of the viewport.
  function scrollToProductGrid() {
    var pillsEl = document.getElementById('collection-style-filters__pills');
    var target = (pillsEl && !pillsEl.hidden) ? pillsEl : document.getElementById('ProductGridContainer');
    if (!target) return;
    var top = target.getBoundingClientRect().top;
    if (top < 0 || top > window.innerHeight * 0.5) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  function onSelectionChange(index) {
    applyVisibility();
    pushStylesToUrl();
    syncPickerState();
    renderPills(index);
    scrollToProductGrid();
  }

  function escapeHtml(str) {
    return String(str || '').replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  // ─── Main init ──────────────────────────────────────────────────────────────
  function init() {
    if (!isDesktop()) return;

    var list = collectEntries();
    if (!list.length) return;

    var uninitialized = list.filter(function (entry) { return !entry.card.hasAttribute('data-style-filter-ready'); });
    if (!uninitialized.length) return;
    uninitialized.forEach(function (entry) { entry.card.setAttribute('data-style-filter-ready', '1'); });

    entries = list;
    selected = new Set(stylesFromLocation());

    resolveCategories(entries).then(function () {
      var index = buildCategoryIndex(entries);
      // Prune typo'd URL tokens so no phantom pill/filter shows, but leave
      // the URL itself untouched (don't rewrite ?styles=).
      pruneSelectedToIndex(index);
      applyVisibility();
      renderPicker(index);
      renderPills(index);
    });
  }

  window.addEventListener('popstate', function () {
    selected = new Set(stylesFromLocation());
    var index = buildCategoryIndex(entries);
    pruneSelectedToIndex(index);
    applyVisibility();
    renderPicker(index);
    renderPills(index);
  });

  window.CollectionStyleFilters = { init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
